import React, { useRef, useState } from 'react';

import { Field } from '../../../../types/common.types';
import { Programme } from '../../../../types/entity.types';

import useTheme from '../../../../hooks/use-theme/useTheme';

import { formatProgrammeDates, getClassNames, isNumeric, validateSouthAfricanIdNumber } from '../../../../utils/util';

import AuthLayout from '../../../../layouts/auth/AuthLayout';
import Button from '../../../common/button/Button';
import Checkbox from '../../../common/input/checkbox/Checkbox';

import './programme-details.scss';

type Props = {
  programme?: Programme;
  fields: Field[];
  updateField: (label: string, value: string | boolean) => void;
  showToaster: (value: string) => void;
  onProceedToPaymentClick: () => void;
};

const ProgrammeDetails = ({ programme, fields, updateField, showToaster, onProceedToPaymentClick }: Props) => {
  const [showConfirmation, setShowConfirmation] = useState(false);

  const { pps } = useTheme();

  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const scrollToField = (label: string) => {
    const container = fieldRefs.current[label];
    const input = container?.querySelector<HTMLInputElement>('input');
    const target = input ?? container?.querySelector<HTMLElement>('.checkbox') ?? container;

    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    input?.focus();
  };

  const handleContinueClick = () => {
    for (const field of fields) {
      if (!field.value && !field.optional) {
        showToaster('Please fill in all required fields');
        scrollToField(field.label);
        return;
      }

      if (field.label === 'ID Number') {
        const validId = validateSouthAfricanIdNumber(field.value as string);
        if (!validId) {
          showToaster('Please provide a valid South African ID Number');
          scrollToField(field.label);
          return;
        }
      }
    }

    setShowConfirmation(!showConfirmation);
  };

  const renderConfirmationValue = (field: Field) => {
    if (field.type === 'checkbox') {
      return field.value ? 'Yes' : 'No';
    }

    return (field.value as string) || '-';
  };

  const handleFieldChange = (field: Field, value: string) => {
    if (field.label === 'ID Number' || field.label === 'Mobile Number') {
      if (value === '') {
        updateField(field.label, value);
        return;
      }

      if (isNumeric(value)) {
        updateField(field.label, value);
      }
    } else {
      updateField(field.label, value);
    }
  };

  return (
    <AuthLayout smallLogo>
      <section className="programme-details">
        {programme && (
          <>
            {showConfirmation && (
              <section className={getClassNames('programme-details__confirmation', { pps })}>
                <h1 className={getClassNames('programme-details__confirmation__heading', { pps })}>
                  Order Confirmation
                </h1>
                <section
                  className={getClassNames('programme-details__confirmation__fields visible-scrollbar', { pps })}
                >
                  {fields.map((field) => (
                    <div className={getClassNames('programme-details__confirmation__field', { pps })} key={field.label}>
                      <p className={getClassNames('programme-details__confirmation__field__label', { pps })}>
                        {field.label}:
                      </p>
                      <p className={getClassNames('programme-details__confirmation__field__value', { pps })}>
                        {renderConfirmationValue(field)}
                      </p>
                    </div>
                  ))}
                </section>
              </section>
            )}

            <section className={getClassNames('programme-details__details', { pps })}>
              <p className={getClassNames('programme-details__details__line-one', { pps })}>You are joining</p>
              <div className={getClassNames('programme-details__details__line-two', { pps })}>
                <p className={getClassNames('programme-details__details__name', { pps })}>{programme.name}</p>
                <p className={getClassNames('programme-details__details__price', { pps })}>
                  R{programme.registrationRequirement?.programmePrice}
                </p>
              </div>
              <p className={getClassNames('programme-details__details__line-three', { pps })}>
                {formatProgrammeDates(programme.fromDate, programme.toDate)}
              </p>
            </section>

            {!showConfirmation && (
              <section className={getClassNames('programme-details__fields visible-scrollbar', { pps })}>
                {fields.map((field) => (
                  <div
                    key={field.label}
                    ref={(el) => (fieldRefs.current[field.label] = el)}
                    className={getClassNames('programme-details__field', { pps })}
                  >
                    <div className={getClassNames('programme-details__field__label-container', { pps })}>
                      <label
                        className={getClassNames('programme-details__field__label', { pps })}
                        htmlFor={field.label}
                      >
                        {field.label}:
                      </label>
                      {field.optional && (
                        <p className={getClassNames('programme-details__field__optional-text', { pps })}>(Optional)</p>
                      )}
                    </div>
                    {field.type === 'text' && (
                      <input
                        className={getClassNames('programme-details__field__text-input', { pps })}
                        type="text"
                        id={field.label}
                        name={field.label}
                        value={field.value as string}
                        onChange={({ target }) => handleFieldChange(field, target.value)}
                      />
                    )}
                    {field.type === 'checkbox' && (
                      <Checkbox value={!!field.value} onChange={(value: boolean) => updateField(field.label, value)} />
                    )}
                  </div>
                ))}
              </section>
            )}
            <section className={getClassNames('programme-details__buttons', { confirmation: showConfirmation, pps })}>
              {showConfirmation ? (
                <Button text="Proceed to Payment" onClick={onProceedToPaymentClick} auth={!pps} />
              ) : (
                <Button text="Continue" onClick={handleContinueClick} auth={!pps} />
              )}
            </section>
          </>
        )}
      </section>
    </AuthLayout>
  );
};

export default ProgrammeDetails;
